import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { AppKey, AppPartnerService, AppPrice } from '@galaxy/marketplace-apps';
import { EditionsService, Release } from '@galaxy/marketplace-apps/v1';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { SelectedTab } from '../product-details-v2.interface';
import { EditionSelectorComponent } from './edition-selector.component';
import { DialogResponse, ResponseType } from './edition-selector.types';

export interface DialogInput {
  partnerId: string;
  marketId: string;
  productId: string;
  childId?: string;
  translationKeys: {
    title: string;
    explanation?: string;
  };
  appURL: string;
  appPrice$: Observable<AppPrice>;
  currency$: Observable<string>;
}

@Injectable({ providedIn: 'root' })
export class EditionSelectorService {
  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private partnerAppService: AppPartnerService,
    private editionsService: EditionsService,
  ) {}

  public openEditionSelector(input: DialogInput): Observable<DialogResponse> {
    const appKeys = [new AppKey({ appId: input.productId })];
    if (input.childId) {
      appKeys.push(new AppKey({ appId: input.childId }));
    }
    return combineLatest([
      this.partnerAppService
        .getMulti(appKeys, input.partnerId, input.marketId, null, true)
        .pipe(catchError(() => of([]))),
      this.editionsService.listEditionsMarketingForApp(input.productId, Release.RELEASE_PUBLISH).pipe(
        map((editionsMarketing) => editionsMarketing || []),
        catchError(() => of([])),
      ),
      input.appPrice$,
    ]).pipe(
      switchMap(([apps, editionsMarketing]) => {
        const product = apps[0];
        const child = apps.length > 1 ? apps[1] : null;
        if (editionsMarketing?.length === 1) {
          return of({
            type: ResponseType.EditionSelected,
            productId: product?.key?.appId,
            editionId: editionsMarketing[0]?.editionId,
          });
        } else if (editionsMarketing?.length > 1) {
          return this.dialog
            .open(EditionSelectorComponent, {
              id: 'edition-selector-dialog',
              maxWidth: `660px`,
              data: {
                ...input,
                editionsMarketing: editionsMarketing,
                translationArgs: {
                  productName: product.sharedMarketingInformation.name,
                  childName: child?.sharedMarketingInformation?.name,
                },
              },
            })
            .afterClosed();
        }
        return of({ type: ResponseType.EditionsUnsupported, productId: product?.key?.appId });
      }),
      tap((resp) => {
        if (resp?.type === ResponseType.CompareEditions) {
          return this.router.navigate([input.appURL], {
            queryParams: {
              tab: SelectedTab.EditionsAndPricing,
            },
          });
        }
      }),
    );
  }
}
